/* eslint-disable react/prop-types */
/**
 * DesignPackPreviewModal — v260.64 / v260.65
 * ==========================================
 * Inline first-page preview for the Handover Pack exports.
 *
 *   • PDF   → native <iframe src={blobUrl}>
 *   • DOCX  → mammoth.convertToHtml → sanitized HTML
 *   • XLSX  → SheetJS XLSX.read → first sheet rendered as <table>
 *
 * Exports both:
 *   • `DesignPackPreviewModal`  (default) — full-screen modal used by
 *     the Design Pack tab's Eye buttons.
 *   • `DesignPackPreviewBody`  (named)    — bare preview body without
 *     the modal chrome, used inline inside other modals (e.g. the
 *     Email Handover Pack modal's "Preview attachments" tab).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, X, Download, FileText, FileSpreadsheet, FileType2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import mammoth from 'mammoth/mammoth.browser';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_META = {
  docx: {
    label: 'Word document',
    path: 'export/docx',
    icon: FileText,
    accent: 'text-blue-600',
  },
  pdf: {
    label: 'PDF',
    path: 'export/pdf',
    icon: FileType2,
    accent: 'text-rose-600',
  },
  xlsx: {
    label: 'Excel checklist',
    path: 'export/checklist.xlsx',
    icon: FileSpreadsheet,
    accent: 'text-emerald-600',
  },
};

// Shared CSS that styles both the mammoth (.docx) and SheetJS (.xlsx)
// HTML output. Kept in this file so the inline reuse from
// EmailHandoverModal picks it up automatically.
const PREVIEW_STYLES = `
  .docx-preview h1 { font-size: 22pt; font-weight: 600; color: #0F2040; margin: 16pt 0 8pt; }
  .docx-preview h2 { font-size: 16pt; font-weight: 600; color: #0F2040; margin: 14pt 0 6pt; }
  .docx-preview h3 { font-size: 13pt; font-weight: 600; color: #334155; margin: 10pt 0 4pt; }
  .docx-preview p { margin: 0 0 6pt; }
  .docx-preview ul { padding-left: 22pt; margin: 0 0 6pt; }
  .docx-preview ol { padding-left: 22pt; margin: 0 0 6pt; }
  .docx-preview li { margin: 0 0 2pt; }
  .docx-preview table { border-collapse: collapse; width: 100%; margin: 8pt 0; }
  .docx-preview table td, .docx-preview table th { border: 1px solid #E2E8F0; padding: 4pt 6pt; vertical-align: top; }
  .docx-preview table th { background: #F8FAFC; font-weight: 600; }
  .docx-preview em { color: #475569; }
  .xlsx-preview table { border-collapse: collapse; }
  .xlsx-preview td, .xlsx-preview th { border: 1px solid #E2E8F0; padding: 4px 8px; text-align: left; vertical-align: top; white-space: pre-wrap; }
  .xlsx-preview tr:first-child td { background: #0F2040; color: #fff; font-weight: 600; }
`;

/**
 * Bare preview body — no modal chrome. Renders the appropriate
 * viewer for `kind` ('docx' | 'pdf' | 'xlsx').
 */
export function DesignPackPreviewBody({ kind, estimateId, height = '60vh', testIdSuffix = '' }) {
  const meta = KIND_META[kind];
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [docxHtml, setDocxHtml] = useState(null);
  const [xlsxSheets, setXlsxSheets] = useState(null);
  const [activeSheet, setActiveSheet] = useState(0);

  const renderBlob = useCallback(async (b) => {
    if (kind === 'pdf') {
      setPdfUrl(URL.createObjectURL(b));
      return;
    }
    const arrayBuffer = await b.arrayBuffer();
    if (kind === 'docx') {
      const result = await mammoth.convertToHtml({ arrayBuffer });
      setDocxHtml(result.value || '<p class="text-slate-400 italic">Empty document.</p>');
      return;
    }
    if (kind === 'xlsx') {
      const wb = XLSX.read(arrayBuffer, { type: 'array' });
      const sheets = wb.SheetNames.map(name => ({
        name,
        html: XLSX.utils.sheet_to_html(wb.Sheets[name], { editable: false }),
      }));
      setXlsxSheets(sheets);
    }
  }, [kind]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await axios.get(
          `${API_URL}/api/estimates/${estimateId}/design-pack/${meta.path}`,
          { withCredentials: true, responseType: 'blob' },
        );
        if (cancelled) return;
        await renderBlob(r.data);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || `${meta.label} preview failed`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [estimateId, meta.path, meta.label, renderBlob]);

  // Cleanup the PDF object URL when the body unmounts.
  useEffect(() => () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
  }, [pdfUrl]);

  const suffix = testIdSuffix ? `-${testIdSuffix}` : '';

  return (
    <div style={{ minHeight: height }} className="bg-slate-50/40 flex flex-col">
      {loading && (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-slate-500 py-8">
          <Loader2 className="w-6 h-6 animate-spin" />
          <div className="text-xs">Rendering {meta.label} preview…</div>
        </div>
      )}

      {!loading && error && (
        <div
          className="flex-1 flex items-center justify-center text-rose-600 text-sm py-8"
          data-testid={`design-pack-preview-error${suffix}`}
        >
          {error}
        </div>
      )}

      {!loading && !error && kind === 'pdf' && pdfUrl && (
        <iframe
          src={pdfUrl}
          title="PDF preview"
          className="w-full bg-white"
          style={{ height }}
          data-testid={`design-pack-preview-pdf-iframe${suffix}`}
        />
      )}

      {!loading && !error && kind === 'docx' && docxHtml !== null && (
        <div
          className="docx-preview bg-white mx-auto my-4 p-8 shadow-sm border border-slate-200 max-w-3xl overflow-auto"
          style={{
            fontFamily: 'Calibri, "Helvetica Neue", Arial, sans-serif',
            fontSize: '11pt',
            lineHeight: 1.5,
            color: '#0F2040',
            maxHeight: height,
          }}
          dangerouslySetInnerHTML={{ __html: docxHtml }}
          data-testid={`design-pack-preview-docx-body${suffix}`}
        />
      )}

      {!loading && !error && kind === 'xlsx' && xlsxSheets && (
        <div className="flex flex-col flex-1" data-testid={`design-pack-preview-xlsx-body${suffix}`}>
          {xlsxSheets.length > 1 && (
            <div className="flex items-center gap-1 px-4 pt-3 border-b border-slate-200 bg-white">
              {xlsxSheets.map((s, i) => (
                <button
                  key={s.name}
                  type="button"
                  onClick={() => setActiveSheet(i)}
                  className={`text-xs px-3 py-1.5 rounded-t border-b-2 transition ${
                    i === activeSheet
                      ? 'border-[#4ABFCF] text-[#0F2040] font-semibold bg-[#4ABFCF]/5'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid={`design-pack-preview-xlsx-sheet${suffix}-${i}`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}
          <div
            className="xlsx-preview overflow-auto bg-white p-4 flex-1"
            style={{ fontSize: '11px', maxHeight: height }}
            dangerouslySetInnerHTML={{ __html: xlsxSheets[activeSheet]?.html || '' }}
          />
        </div>
      )}

      <style>{PREVIEW_STYLES}</style>
    </div>
  );
}

/**
 * Modal wrapper used by the Design Pack tab's Eye buttons. Owns the
 * blob fetch directly so it can drive the in-modal "Download" CTA
 * without re-hitting the network.
 */
export default function DesignPackPreviewModal({ kind, estimateId, onClose, onDownload }) {
  const meta = KIND_META[kind];
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [docxHtml, setDocxHtml] = useState(null);
  const [xlsxSheets, setXlsxSheets] = useState(null);
  const [activeSheet, setActiveSheet] = useState(0);
  const [blob, setBlob] = useState(null);

  const renderBlob = useCallback(async (b) => {
    if (kind === 'pdf') {
      setPdfUrl(URL.createObjectURL(b));
      return;
    }
    const arrayBuffer = await b.arrayBuffer();
    if (kind === 'docx') {
      const result = await mammoth.convertToHtml({ arrayBuffer });
      setDocxHtml(result.value || '<p class="text-slate-400 italic">Empty document.</p>');
      return;
    }
    if (kind === 'xlsx') {
      const wb = XLSX.read(arrayBuffer, { type: 'array' });
      const sheets = wb.SheetNames.map(name => ({
        name,
        html: XLSX.utils.sheet_to_html(wb.Sheets[name], { editable: false }),
      }));
      setXlsxSheets(sheets);
    }
  }, [kind]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await axios.get(
          `${API_URL}/api/estimates/${estimateId}/design-pack/${meta.path}`,
          { withCredentials: true, responseType: 'blob' },
        );
        if (cancelled) return;
        setBlob(r.data);
        await renderBlob(r.data);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || `${meta.label} preview failed`);
        toast.error(`${meta.label} preview failed`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [estimateId, meta.path, meta.label, renderBlob]);

  useEffect(() => () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
  }, [pdfUrl]);

  const handleDownload = useCallback(() => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `design_pack.${kind === 'xlsx' ? 'xlsx' : kind}`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
    onDownload?.();
  }, [blob, kind, onDownload]);

  const Icon = meta.icon;

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="design-pack-preview-modal"
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50/60">
          <div className="flex items-center gap-2">
            <Icon className={`w-5 h-5 ${meta.accent}`} />
            <div>
              <div className="text-sm font-semibold text-[#0F2040]">{meta.label} preview</div>
              <div className="text-xs text-slate-500">First-page snapshot · sanity-check before sending</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              disabled={!blob}
              data-testid="design-pack-preview-download-btn"
            >
              <Download className="w-3.5 h-3.5 mr-1" /> Download
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              data-testid="design-pack-preview-close-btn"
              className="text-slate-500 hover:text-slate-700"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-slate-50/40">
          {loading && (
            <div className="h-[60vh] flex flex-col items-center justify-center gap-2 text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin" />
              <div className="text-xs">Rendering {meta.label} preview…</div>
            </div>
          )}

          {!loading && error && (
            <div className="h-[40vh] flex items-center justify-center text-rose-600 text-sm" data-testid="design-pack-preview-error">
              {error}
            </div>
          )}

          {!loading && !error && kind === 'pdf' && pdfUrl && (
            <iframe
              src={pdfUrl}
              title="PDF preview"
              className="w-full h-[70vh] bg-white"
              data-testid="design-pack-preview-pdf-iframe"
            />
          )}

          {!loading && !error && kind === 'docx' && docxHtml !== null && (
            <div
              className="docx-preview bg-white mx-auto my-4 p-8 shadow-sm border border-slate-200 max-w-3xl"
              style={{
                fontFamily: 'Calibri, "Helvetica Neue", Arial, sans-serif',
                fontSize: '11pt',
                lineHeight: 1.5,
                color: '#0F2040',
                minHeight: '60vh',
              }}
              dangerouslySetInnerHTML={{ __html: docxHtml }}
              data-testid="design-pack-preview-docx-body"
            />
          )}

          {!loading && !error && kind === 'xlsx' && xlsxSheets && (
            <div className="flex flex-col h-full" data-testid="design-pack-preview-xlsx-body">
              {xlsxSheets.length > 1 && (
                <div className="flex items-center gap-1 px-4 pt-3 border-b border-slate-200 bg-white">
                  {xlsxSheets.map((s, i) => (
                    <button
                      key={s.name}
                      type="button"
                      onClick={() => setActiveSheet(i)}
                      className={`text-xs px-3 py-1.5 rounded-t border-b-2 transition ${
                        i === activeSheet
                          ? 'border-[#4ABFCF] text-[#0F2040] font-semibold bg-[#4ABFCF]/5'
                          : 'border-transparent text-slate-500 hover:text-slate-700'
                      }`}
                      data-testid={`design-pack-preview-xlsx-sheet-${i}`}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              )}
              <div
                className="xlsx-preview overflow-auto bg-white p-4 flex-1"
                style={{ fontSize: '11px' }}
                dangerouslySetInnerHTML={{ __html: xlsxSheets[activeSheet]?.html || '' }}
              />
            </div>
          )}
        </div>
      </div>

      <style>{PREVIEW_STYLES}</style>
    </div>
  );
}
